import { Extension, isMacOS } from '@tiptap/core';
import { Slice, Fragment, Schema } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { insertImages } from '../utils/imageInsert';
import { getImageFilesFromItems } from '../utils/imageTransfer';

export type TTransformFragment = (fragment: Fragment, schema: Schema) => Fragment;

interface Options {
  transformFragments: TTransformFragment[];
}

// prosemirror-view's own copy modifier: `const dragCopyModifier = mac ? 'altKey' : 'ctrlKey'`.
const dragCopyModifier = isMacOS() ? 'altKey' : 'ctrlKey';

const applyFragmentTransforms = (slice: Slice, schema: Schema, transforms: TTransformFragment[]): Slice => {
  let content = slice.content;
  for (let i = 0; i < transforms.length; i++) {
    try {
      content = transforms[i](content, schema);
    } catch (e) {
      console.error(`Paste script ${i} failed!`, e);
    }
  }
  return new Slice(content, slice.openStart, slice.openEnd);
};

const PasteRule = Extension.create<Options>({
  name: 'paste-rule',

  addOptions() {
    return {
      transformFragments: [],
    };
  },

  addProseMirrorPlugins() {
    const { transformFragments } = this.options;
    let isMoveDrop = false;
    return [
      ...(this.parent?.() || []),

      new Plugin({
        key: new PluginKey('paste-rule'),

        props: {
          handleDOMEvents: {
            paste: () => {
              isMoveDrop = false;
              return false;
            },
            // prosemirror decides move-or-copy from the DROP event but does not tell transformPasted,
            // so read it here - this runs before prosemirror's own handler clears `view.dragging`.
            drop: (view, event) => {
              isMoveDrop = !!view.dragging && !event[dragCopyModifier];
              return false;
            },
          },

          // Non-images stay false so prosemirror-tables' cell-paste handler runs.
          handlePaste(view, event) {
            const imgFiles = getImageFilesFromItems(event.clipboardData?.items);
            if (!imgFiles.length) return false;
            event.preventDefault();
            return insertImages({ view, files: imgFiles, eventMeta: 'paste' });
          },

          // Only a move keeps its ids - it is the same content in a new place. A copy is a new thing.
          transformPasted(slice, view) {
            if (isMoveDrop) return slice;
            return applyFragmentTransforms(slice, view.state.schema, transformFragments);
          },
        },
      }),
    ];
  },
});

export default PasteRule;
